import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Card from "./Card";
import SeatPanelCard from "./SeatPanelCard";
import { formatStackBB } from "./helpers";
import { ThemeContext } from "../../../context/ThemeContext";

function clamp(min, v, max) {
  return Math.max(min, Math.min(max, v));
}

/**
 * Sloty (x%, y%) – mapy "estetyczne" jak w Twoim wcześniejszym pliku.
 * UWAGA: kolejność elementów w mapach NIE jest "po okręgu" – to jest celowo,
 * bo chcesz zachować wcześniejszy "szyk" (np. 3 panele obok siebie u góry dla 4 graczy).
 *
 * Poprawną kolejność clockwise robimy osobno przez permutację indeksów slotów (patrz getSeatSlotPermutation).
 */
function getSeatSlots({ mode, n, bottomSafeYPct, crowded = false }) {
  const S = (xPct, yPct) => ({ xPct, yPct });

  const defaultBottomSafe = mode === "NARROW" ? 46 : mode === "MID" ? 74 : 76;
  const maxY = typeof bottomSafeYPct === "number" ? bottomSafeYPct : defaultBottomSafe;

  // --- WIDE (stageW >= 900)
  // UWAGA: układ jak w Twoim wcześniejszym pliku (TOP-y pierwsze itd.)
  const WIDE = {
    7: !crowded
      ? [
          // 3 TOP + 2 LEFT + 2 RIGHT (kolumny boczne równe X)
          S(28, 14), S(50, 14), S(72, 14),
          S(12, 30), S(12, 66),
          S(88, 30), S(88, 66),
        ]
      : [
          S(30, 13), S(50, 13), S(70, 13),
          S(12, 28), S(12, 64),
          S(88, 28), S(88, 64),
        ],

    6: [
      // 2 TOP + 2 LEFT + 2 RIGHT (symetria)
      S(40, 15), S(60, 15),
      S(12, 34), S(12, 66),
      S(88, 34), S(88, 66),
    ],

    5: [
      S(32, 16), S(50, 16), S(68, 16),
      S(14, 52), S(86, 52),
    ],

    4: [
      S(40, 16), S(60, 16),
      S(14, 54), S(86, 54),
    ],

    3: [
      S(32, 16), S(50, 16), S(68, 16),
    ],

    2: [S(40, 16), S(60, 16)],
    1: [S(50, 16)],
  };

  // --- MID (650..899)
  const MID = {
    7: !crowded
      ? [
          // 3 TOP + 2 LEFT + 2 RIGHT (kolumny boczne równe X)
          S(30, 15), S(50, 15), S(70, 15),
          S(14, 38), S(14, 64),
          S(86, 38), S(86, 64),
        ]
      : [
          S(34, 15), S(50, 15), S(66, 15),
          S(14, 32), S(14, 58),
          S(86, 32), S(86, 58),
        ],

    6: [
      S(40, 15), S(60, 15),
      S(14, 34), S(14, 64),
      S(86, 34), S(86, 64),
    ],

    5: [
      S(30, 15), S(50, 15), S(70, 15),
      S(14, 52), S(86, 52),
    ],

    4: [
      S(40, 15), S(60, 15),
      S(14, 54), S(86, 54),
    ],

    3: [
      S(31, 15), S(50, 15), S(69, 15),
    ],

    2: [S(40, 15), S(60, 15)],
    1: [S(50, 15)],
  };

  // --- NARROW / PORTRAIT (telefon)
  const NARROW = {
    7: [
      S(50, 14),
      S(22, 20), S(16, 30), S(22, 40),
      S(78, 20), S(84, 30), S(78, 40),
    ],
    6: [
      S(22, 20), S(16, 30), S(22, 40),
      S(78, 20), S(84, 30), S(78, 40),
    ],
    5: [
      S(50, 14),
      S(20, 24), S(16, 38),
      S(80, 24), S(84, 38),
    ],
    4: [
      S(20, 22), S(16, 38),
      S(80, 22), S(84, 38),
    ],
    3: [
      S(50, 14),
      S(18, 36),
      S(82, 36),
    ],
    2: [
      S(18, 34),
      S(82, 34),
    ],
    1: [S(50, 14)],
  };

  const table = mode === "WIDE" ? WIDE : mode === "MID" ? MID : NARROW;
  const raw = table[n] ? table[n].slice(0, n) : null;
  if (!raw) return null;

  return raw.map(({ xPct, yPct }) => ({
    xPct,
    yPct: Math.min(yPct, maxY),
  }));
}

/**
 * Permutacja indeksów slotów tak, aby:
 * - zachować dotychczasowy "szyk" (mapy getSeatSlots bez zmian),
 * - ale przypisać graczy w kolejności clockwise z perspektywy gracza:
 *   1. (mySeat+1) trafia na "lewy" slot,
 *   2. kolejne idą dalej zgodnie z ruchem wskazówek.
 *
 * Zwraca tablicę długości n, gdzie perm[i] = index slotu dla i-tego przeciwnika w orderedSeatNos.
 */
function getSeatSlotPermutation(mode, n) {
  // WIDE/MID mają bardzo podobny "szyk" (TOP-y pierwsze, potem lewa/prawa).
  if (mode === "WIDE" || mode === "MID") {
    switch (n) {
      case 7:
        // slots: [T_L, T_C, T_R, L_UP, L_LOW, R_UP, R_LOW]
        // order wanted: L_LOW, L_UP, T_L, T_C, T_R, R_UP, R_LOW
        return [4, 3, 0, 1, 2, 5, 6];
      case 6:
        // slots: [T_L, T_R, L_UP, L_LOW, R_UP, R_LOW]
        // order: L_LOW, L_UP, T_L, T_R, R_UP, R_LOW
        return [3, 2, 0, 1, 4, 5];
      case 5:
        // slots: [T_L, T_C, T_R, L, R]
        // order: L, T_L, T_C, T_R, R
        return [3, 0, 1, 2, 4];
      case 4:
        // slots: [T_L, T_R, L, R]
        // order: L, T_L, T_R, R
        return [2, 0, 1, 3];
      case 3:
        // slots: [T_L, T_C, T_R]
        // order: T_L, T_C, T_R (czytelny "szyk" 3 u góry)
        return [0, 1, 2];
      case 2:
        // slots: [T_L, T_R]
        return [0, 1];
      case 1:
        return [0];
      default:
        return null;
    }
  }

  // NARROW ma inny "szyk" (top + łuki boczne)
  if (mode === "NARROW") {
    switch (n) {
      case 7:
        // slots: [TOP, L1, L2, L3, R1, R2, R3]
        // order: L3, L2, L1, TOP, R1, R2, R3
        return [3, 2, 1, 0, 4, 5, 6];
      case 6:
        // slots: [L1, L2, L3, R1, R2, R3]
        // order: L3, L2, L1, R1, R2, R3
        return [2, 1, 0, 3, 4, 5];
      case 5:
        // slots: [TOP, L1, L2, R1, R2]
        // order: L2, L1, TOP, R1, R2
        return [2, 1, 0, 3, 4];
      case 4:
        // slots: [L1, L2, R1, R2]
        // order: L2, L1, R1, R2
        return [1, 0, 2, 3];
      case 3:
        // slots: [TOP, L, R]
        // order: L, TOP, R
        return [1, 0, 2];
      case 2:
        // slots: [L, R]
        return [0, 1];
      case 1:
        return [0];
      default:
        return null;
    }
  }

  return null;
}

export default function PokerTableVisual({
  seats = [],
  seatState = {},
  mySeatNo,
  dealerSeat,
  sbSeat,
  bbSeat,
  actionSeat,
  lastActionBySeat = {},
  board = [],
  pot = 0,
  street = "preflop",
  handId,

  // ✅ end-of-hand result
  showHandResult = false,
  bySeat = null,
  payouts = null,

  // stack/amount display
  stackViewMode = "chips",
  bbAmount = 0,
  maxSeats = 8,
  myHole = null,
  hideMyHoleInSeat = false,
  hideMySeatInTable = false,
  renderActionPanel = null,
  showdownHoles = null,
  onShowCards = null,
  extraBottomPx = 0,
  railAvoidPx = 0,
}) {
  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === "dark";

  const handDealt = Number(handId || 0) > 0;

  // ✅ winners set for UI highlight
  const winnersSet = useMemo(() => {
    if (!showHandResult || !payouts) return new Set();
    const s = new Set();
    Object.keys(payouts).forEach((k) => {
      const v = Number(payouts[k] || 0);
      if (v > 0) s.add(Number(k));
    });
    return s;
  }, [showHandResult, payouts]);

  const rootRef = useRef(null);
  const sceneRef = useRef(null);
  const boardRef = useRef(null);
  const topMeasureRef = useRef(null);

  const [sceneHPx, setSceneHPx] = useState(0);
  const [stageW, setStageW] = useState(980);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 980);
  const [vh, setVh] = useState(typeof window !== "undefined" ? window.innerHeight : 700);

  const [extraTopPx, setExtraTopPx] = useState(0);
  const [boardDownPct, setBoardDownPct] = useState(0);

  useLayoutEffect(() => {
    const el = sceneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r?.height) setSceneHPx(Math.round(r.height));
    });

    ro.observe(el);
    const r0 = el.getBoundingClientRect();
    if (r0?.height) setSceneHPx(Math.round(r0.height));

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r?.width) setStageW(Math.round(r.width));
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

    // ===== Board reveal (futurystyczne "drukowanie" od dołu) =====
  const [boardRevealCount, setBoardRevealCount] = useState(0);
  const revealTimersRef = useRef([]);

  const clearRevealTimers = () => {
    revealTimersRef.current.forEach((id) => clearTimeout(id));
    revealTimersRef.current = [];
  };

  useEffect(() => {
    // reset na nowe rozdanie lub cofnięcie boarda
    clearRevealTimers();

    const b = Array.isArray(board) ? board.filter(Boolean) : [];
    const targetLen = Math.min(5, b.length);

    // docelowo ile kart ma być widocznych zależnie od street
    const targetByStreet =
      street === "preflop" ? 0 :
      street === "flop" ? 3 :
      street === "turn" ? 4 :
      street === "river" ? 5 :
      street === "showdown" ? 5 :
      street === "complete" ? 5 :
      targetLen;

    const target = Math.min(targetByStreet, targetLen);

    // jeśli board się cofnął (np. next hand) -> od razu 0
    if (target === 0) {
      setBoardRevealCount(0);
      return;
    }

    // Jeśli już wcześniej mieliśmy więcej (np. reconnect), nie animuj wstecz
    setBoardRevealCount((prev) => Math.min(prev, target));

    // Animacja:
    // - flop: 3 jednocześnie przez 1000ms (czyli odsłaniamy 3 naraz)
    // - turn/river: odsłaniamy +1 (też przez 1000ms)
    if (street === "flop") {
      // pokazujemy 3 “na raz”: ustaw count na 3 od razu,
      // ale same karty mają animację wipeUp
      setBoardRevealCount(3);
      return;
    }

    if (street === "turn") {
      // odsłoń 4-tą
      setBoardRevealCount(4);
      return;
    }

    if (street === "river" || street === "showdown" || street === "complete") {
      // odsłoń 5-tą
      setBoardRevealCount(5);
      return;
    }

    // fallback
    setBoardRevealCount(target);

    return () => clearRevealTimers();
  }, [street, board, /* opcjonalnie */]);

  const isPortrait = vh > vw;

  const isNarrow = isPortrait || Number(stageW || 0) < 560 || vw < 520;
  const isWide = !isNarrow && Number(stageW || 0) >= 900;
  const mode = isNarrow ? "NARROW" : isWide ? "WIDE" : "MID";

  const SCENE_PAD_TOP = 10;
  const SCENE_PAD_BOTTOM = 5;

  const colors = useMemo(() => {
    const wrapBg = isDark ? "rgb(10, 14, 40)" : "rgb(212, 244, 255)";

    const feltA = isDark ? "rgba(16, 70, 48, 0.98)" : "rgba(175, 236, 196, 0.98)";
    const feltB = isDark ? "rgba(6, 42, 28, 0.98)" : "rgba(86, 186, 118, 0.98)";

    const vignette = isDark
      ? "radial-gradient(circle at 50% 50%, rgba(255,165,0,0.10), rgba(10,14,40,0.55) 55%, rgba(10,14,40,0.92) 100%)"
      : "radial-gradient(circle at 50% 50%, rgba(255,165,0,0.08), rgba(212,244,255,0.88) 55%, rgba(212,244,255,1) 100%)";

    const shadow = isDark ? "0 18px 55px rgba(0,0,0,0.55)" : "0 18px 55px rgba(0,0,0,0.14)";
    return { wrapBg, feltA, feltB, vignette, shadow };
  }, [isDark]);

  const seatMetaByNo = useMemo(() => {
    const m = new Map();
    (seats || []).forEach((s) => {
      const no = Number(s?.seat);
      if (!Number.isFinite(no)) return;
      m.set(no, s);
    });
    return m;
  }, [seats]);

  // ✅ Layout seats: to jest to, co przychodzi z PokerTableViewVisual (w tym placeholdery admina)
  // i to MA wpływać na liczbę miejsc/układ przy testach.
  const layoutSeatNos = useMemo(() => {
    const list = (seats || [])
      .map((s) => Number(s?.seat))
      .filter((x) => Number.isFinite(x))
      .filter((x) => x >= 1 && x <= Number(maxSeats || 8));

    return Array.from(new Set(list)).sort((a, b) => a - b);
  }, [seats, maxSeats]);

  // ✅ perspektywa gracza:
  // - gracz jest na dole (render osobno),
  // - po jego LEWEJ jest mySeat+1 (wrap), dalej +2, +3... po okręgu.
  const orderedSeatNos = useMemo(() => {
    if (!layoutSeatNos.length) return [];

    const max = Number(maxSeats || 8);
    const me = Number(mySeatNo);

    // jeśli admin/test bez siedzenia – kotwica: najmniejszy seat z layoutu
    const anchor = Number.isFinite(me) ? me : Math.min(...layoutSeatNos);

    // start od "lewego sąsiada"
    const start = (anchor % max) + 1;

    const ring = [];
    for (let i = 0; i < max; i++) {
      const seatNo = ((start - 1 + i) % max) + 1;
      ring.push(seatNo);
    }

    return ring.filter((s) => layoutSeatNos.includes(s) && Number(s) !== Number(anchor));
  }, [layoutSeatNos, mySeatNo, maxSeats]);

  const n = orderedSeatNos.length;

  const fmtLast = (seatNo) => {
    const a = lastActionBySeat?.[String(seatNo)];
    if (!a || !a.type) return null;

    const t = String(a.type).toUpperCase();
    const amt = Number.isFinite(Number(a.amount)) ? Number(a.amount) : null;

    if (t === "FOLD") return "FOLD";
    if (t === "CHECK") return "CHECK";
    if (t === "CALL") return amt != null ? `CALL ${amt}` : "CALL";
    if (t === "BET") return amt != null ? `BET ${amt}` : "BET";
    if (t === "RAISE_TO") return amt != null ? `RAISE ${amt}` : "RAISE";
    if (t === "RAISE") return amt != null ? `RAISE ${amt}` : "RAISE";
    return amt != null ? `${t} ${amt}` : t;
  };

  const baseW0 = 111;
  const seatWBase = baseW0;

  const baseCenter = mode === "NARROW" ? 58 : mode === "MID" ? 52 : 50;
  const TABLE_CENTER_Y = clamp(42, baseCenter + (Number(boardDownPct) || 0), 62);

  const slotList = useMemo(() => {
    const h = Math.max(1, Number(sceneHPx || 0));
    const avoidPx = Math.max(0, Number(railAvoidPx || 0)) + Math.max(0, Number(extraBottomPx || 0)) + 12;
    const avoidPct = (avoidPx / h) * 100;
    const bottomSafeYPct = clamp(30, 100 - avoidPct, 90);

    const crowded = n >= 7;
    return getSeatSlots({ mode, n, bottomSafeYPct, crowded });
  }, [mode, n, sceneHPx, extraBottomPx, railAvoidPx]);

  // fallback elipsa
  const crowdedFallback = isNarrow;
  const radiusX = n <= 3 ? 41 : n <= 6 ? 40 : 39;
  const radiusY = crowdedFallback ? (n <= 3 ? 30 : n <= 6 ? 32 : 31) : n === 2 ? 24 : n === 3 ? 26 : n <= 6 ? 27 : 26;
  const startDeg = 90;
  const stepDeg = n > 0 ? -360 / n : 0;

  const positioned = useMemo(() => {
    if (n === 0) return [];

    const perm = getSeatSlotPermutation(mode, n);

    if (Array.isArray(slotList) && slotList.length === n) {
      return orderedSeatNos.map((seatNo, i) => {
        const slotIdx = Array.isArray(perm) && perm[i] != null ? perm[i] : i;
        const s = slotList[slotIdx] || slotList[i];
        return { seatNo, xPct: s.xPct, yPct: s.yPct };
      });
    }

    return orderedSeatNos.map((seatNo, i) => {
      const deg = startDeg + i * stepDeg;
      const rad = (deg * Math.PI) / 180;

      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      let x = 50 + radiusX * cos;
      let y = 50 + radiusY * sin - 3;

      if (sin < -0.92) y -= 6;
      else if (sin < -0.8) y -= 5;
      else if (sin < -0.65) y -= 4;

      y = Math.max(crowdedFallback ? 18 : 14, y);

      if (railAvoidPx > 0 && sceneHPx > 0) {
        const avoidPct = (railAvoidPx / sceneHPx) * 100;
        const weight = sin > 0 ? 0.55 + 0.45 * sin : Math.abs(sin) < 0.35 ? 0.25 : 0;
        const k = crowdedFallback ? 1.0 : 0.85;
        y -= avoidPct * weight * k;

        const bottomCap = 100 - avoidPct - (crowdedFallback ? 7 : 4);
        y = Math.min(y, bottomCap);
        y = Math.max(crowdedFallback ? 18 : 14, y);
      }

      return { seatNo, xPct: x, yPct: y };
    });
  }, [orderedSeatNos, mode, n, slotList, radiusX, radiusY, stepDeg, railAvoidPx, sceneHPx, crowdedFallback]);

  const topRowSeatsSet = useMemo(() => {
    if (!positioned.length) return new Set();
    const minY = Math.min(...positioned.map((p) => p.yPct));
    const eps = 0.6;
    return new Set(positioned.filter((p) => p.yPct <= minY + eps).map((p) => Number(p.seatNo)));
  }, [positioned]);

  const measureSeatNo = useMemo(() => {
    if (!positioned.length) return null;
    const minY = Math.min(...positioned.map((p) => p.yPct));
    const top = positioned.filter((p) => p.yPct <= minY + 0.6);
    if (!top.length) return Number(positioned[0]?.seatNo) || null;
    top.sort((a, b) => Math.abs(a.xPct - 50) - Math.abs(b.xPct - 50));
    return Number(top[0]?.seatNo) || null;
  }, [positioned]);

  useLayoutEffect(() => {
    const sceneEl = sceneRef.current;
    if (!sceneEl) return;

    if (n === 0) {
      setExtraTopPx(0);
      setBoardDownPct(0);
      return;
    }

    let raf1 = 0;
    let raf2 = 0;

    const measure = () => {
      const seatEl = topMeasureRef.current;
      if (!seatEl) return;

      const sceneRect = sceneEl.getBoundingClientRect();
      const seatRect = seatEl.getBoundingClientRect();

      const seatRects = (() => {
        try {
          const nodes = Array.from(sceneEl.querySelectorAll('[data-seat-root="1"]'));
          const rects = nodes.map((nd) => nd.getBoundingClientRect()).filter((r) => r && r.width > 0 && r.height > 0);
          return rects.length ? rects : [seatRect];
        } catch {
          return [seatRect];
        }
      })();

      const TOP_CLEARANCE = isNarrow ? (n === 1 ? 16 : 12) : n === 1 ? 12 : 10;
      const TOP_MARGIN = 2;

      const minTop = Math.min(...seatRects.map((r) => r.top));
      const maxBottom = Math.max(...seatRects.map((r) => r.bottom));

      const needTop = sceneRect.top + TOP_CLEARANCE - minTop;
      const overflowTop = Math.max(0, Math.ceil(needTop) + TOP_MARGIN);

      const nextTop = clamp(0, overflowTop, isNarrow ? 620 : 520);
      setExtraTopPx((prev) => (Math.abs(prev - nextTop) >= 1 ? nextTop : prev));

      const boardEl = boardRef.current;
      if (boardEl) {
        const boardRect = boardEl.getBoundingClientRect();
        const BOARD_CLEARANCE = isNarrow ? (n === 1 ? 16 : 12) : n === 1 ? 12 : 10;

        const overlapPx = maxBottom - (boardRect.top - BOARD_CLEARANCE);
        const needDownPct =
          overlapPx > 0 && sceneRect.height > 0
            ? clamp(0, (overlapPx / sceneRect.height) * 100, isNarrow ? (n === 1 ? 20 : 16) : n === 1 ? 16 : 12)
            : 0;

        setBoardDownPct((prev) => {
          const next = needDownPct;
          return Math.abs((prev || 0) - next) >= 0.2 ? next : prev;
        });
      } else {
        setBoardDownPct(0);
      }
    };

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [n, isNarrow, stageW, positioned, seatState, board, street, showdownHoles, myHole, pot, actionSeat, lastActionBySeat]);

  const boardShown = useMemo(() => (Array.isArray(board) ? board.slice(0, 5) : []), [board]);

  const renderBoardFive = () => {
    const arr = [];
    for (let i = 0; i < 5; i++) {
      const c = boardShown[i];

      const isRevealed = i < boardRevealCount;
      const shouldAnimate =
        isRevealed &&
        ((street === "flop" && i < 3) ||
          (street === "turn" && i === 3) ||
          (street === "river" && i === 4));

      if (c && isRevealed) {
        arr.push(
          <Card
            key={`b-${i}-${street}`}
            c={c}
            size="sm"
            revealMode={shouldAnimate ? "wipeUp" : "none"}
            revealMs={3000}
            revealDelayMs={street === "flop" && i < 3 ? i * 160 : 0}
          />
        );
      } else {
        arr.push(<div key={`b-${i}`} className="poker-card-rect poker-card-rect--sm poker-card-rect--empty" />);
      }
    }
    return arr;
  };

  const renderSeat = ({ seatNo, xPct, yPct }) => {
    const key = String(seatNo);

    // ✅ wynik rozdania dla tego seata
    const h = bySeat?.[key] || null;
    const delta = Number(payouts?.[key] || 0);
    const st = seatState?.[key] || {};

    const isMe = mySeatNo != null && Number(mySeatNo) === Number(seatNo);
    if (isMe && hideMySeatInTable) return null;

    const isDealer = dealerSeat != null && Number(dealerSeat) === Number(seatNo);
    const isSmallBlind = sbSeat != null && Number(sbSeat) === Number(seatNo);
    const isBigBlind = bbSeat != null && Number(bbSeat) === Number(seatNo);
    const isAction = actionSeat != null && Number(actionSeat) === Number(seatNo);

    const folded = !!st.folded;
    const inHand = !!st.inHand;
    const stack = st.stack ?? "-";
    const allIn = inHand && !folded && Number(stack || 0) === 0;

    const last = fmtLast(seatNo);

    const isTopRow = topRowSeatsSet.has(Number(seatNo));
    const seatW = seatWBase;

    const layoutVariant = isMe ? "twoColumn" : "stacked";

    const background = isDark
      ? folded
        ? "rgba(0,0,0,0.78)"
        : "rgba(0,0,0,0.86)"
      : folded
      ? "rgba(255,255,255,0.78)"
      : "rgba(255,255,255,0.92)";

    const border = isAction ? "2px solid rgba(255,165,0,0.95)" : "1px solid rgba(255,165,0,0.55)";
    const shadow = isAction ? "0 0 16px rgba(255,165,0,0.18)" : "0 0 10px rgba(0,0,0,0.18)";

    const meta = seatMetaByNo.get(Number(seatNo)) || {};
    const displayName = meta?.name || meta?.nick || meta?.username || meta?.login || meta?.email || `Miejsce ${seatNo}`;

    const publicCards = showdownHoles?.[key] || null;
    const hasPublic = Array.isArray(publicCards) && publicCards.length === 2;

    const canRevealPhase = street === "showdown" || street === "complete";
    const showShowBtn = !!onShowCards && isMe && canRevealPhase && !hasPublic;

    const myCards = Array.isArray(myHole) ? myHole.filter(Boolean) : [];
    const showMyCards = isMe && myCards.length === 2;

    const seatCardSize = isMe ? (hideMyHoleInSeat ? "xxs" : "xs") : "xxs";

    const stageWNow = Math.max(1, Number(stageW || 980));

    // ✅ mniej "wpychania do środka" na wąskim ekranie (bez zwężania paneli)
    const clampPadPx =
      mode === "NARROW" ? 8 :
      (n >= 6 || stageWNow < 720) ? 6 :
      10;

    const halfPct = ((seatW / 2 + clampPadPx) / stageWNow) * 100;
    const xClamped = clamp(halfPct, xPct, 100 - halfPct);

    const posStyle = isMe
      ? { position: "absolute", left: "50%", bottom: 6, transform: "translateX(-50%)" }
      : {
          position: "absolute",
          left: `${xClamped}%`,
          top: `${yPct}%`,
          transform: "translate(-50%, -50%)",
        };

    return (
      <SeatPanelCard
        style={posStyle}
        outerRef={measureSeatNo != null && Number(measureSeatNo) === Number(seatNo) ? topMeasureRef : null}
        seatNo={seatNo}
        isDark={isDark}
        isPlaceholder={!!meta?.__placeholder}
        displayName={displayName}
        isDealer={isDealer}
        isSmallBlind={isSmallBlind}
        isBigBlind={isBigBlind}
        isWinner={winnersSet.has(Number(seatNo))}
        isAction={isAction}
        folded={folded}
        inHand={inHand}
        stack={stack}
        committedStreet={st?.committedStreet ?? 0}
        stackViewMode={stackViewMode}
        bbAmount={bbAmount}
        lastActionText={last || null}
        allIn={allIn}
        isMe={isMe}
        hideMyHoleInSeat={hideMyHoleInSeat}
        showMyCards={showMyCards}
        myCards={myCards}
        hasPublic={hasPublic}
        publicCards={publicCards}
        seatCardSize={seatCardSize}
        showShowBtn={showShowBtn}
        onShowCards={onShowCards}
        widthPx={seatW}
        background={background}
        border={border}
        shadow={shadow}
        layoutVariant={layoutVariant}
        allowActionWrap={isTopRow}
        handDealt={handDealt}

        // ✅ wynik rozdania
        showHandResult={showHandResult}
        handDescr={h?.descr || h?.name || null}
        handDelta={delta}
      />
    );
  };

  const actionOpen = !!renderActionPanel || Number(extraBottomPx || 0) > 0 || Number(railAvoidPx || 0) > 0;

  const seatsCrowded = n >= 6;
  const widthCrowded = mode === "MID";
  const crowdNeeded = !isNarrow && (seatsCrowded || widthCrowded);

  const baseCrowdBoost = !crowdNeeded ? 0 : n >= 7 ? 170 : n === 6 ? 95 : 0;
  const actionBoost = !isNarrow && actionOpen ? (n >= 7 ? 80 : 55) : 0;

  const crowdBoost = baseCrowdBoost + actionBoost;

  const sceneHeight = isNarrow
    ? "clamp(700px, 92vh, 1100px)"
    : isPortrait
    ? `clamp(${560 + crowdBoost}px, 78vh, ${900 + crowdBoost}px)`
    : `clamp(${420 + crowdBoost}px, 58vh, ${560 + crowdBoost}px)`;

  const safeExtraBottom = Math.max(0, Math.round(Number(extraBottomPx || 0)));
  const safeExtraTop = Math.max(0, Math.round(Number(extraTopPx || 0)));

  return (
    <div
      ref={rootRef}
      style={{
        width: "100%",
        position: "relative",
        paddingBottom: safeExtraBottom,
        paddingTop: safeExtraTop,
        borderRadius: 28,
        background: colors.wrapBg,
        boxShadow: colors.shadow,
        overflow: "visible",
      }}
    >
      <div
        className="ptv-clip"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 28,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: colors.vignette }} />
      </div>

      <div
        ref={sceneRef}
        className="ptv-scene"
        style={{
          position: "relative",
          zIndex: 1,
          height: sceneHeight,
          overflow: "visible",
          borderRadius: 28,
          paddingTop: SCENE_PAD_TOP,
          paddingBottom: SCENE_PAD_BOTTOM,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: `${TABLE_CENTER_Y}%`,
            width: isNarrow ? "82%" : "84%",
            height: isNarrow ? "70%" : "62%",
            transform: "translate(-50%, -50%)",
            borderRadius: "999px",
            background: `radial-gradient(circle at 50% 40%, ${colors.feltA}, ${colors.feltB} 70%)`,
            border: "2px solid rgba(255,165,0,0.20)",
            boxShadow: "0 0 0 10px rgba(0,0,0,0.12) inset, 0 0 35px rgba(0,0,0,0.35)",
          }}
        />

        <div
          ref={boardRef}
          className="ptv-pot-block"
          style={{
            position: "absolute",
            left: "50%",
            top: `${TABLE_CENTER_Y}%`,
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 6 }}>{renderBoardFive()}</div>

          <div style={{ fontSize: 12, opacity: 0.9 }}>
            <b>{street}</b>
          </div>

          <div style={{ marginTop: 4, fontSize: 14, fontWeight: 900 }}>
            Pula: <span style={{ color: "rgba(255,165,0,0.95)" }}>{stackViewMode === "bb" ? `${formatStackBB(pot, bbAmount)} BB` : pot}</span>
          </div>
        </div>

        {positioned.map(renderSeat)}
      </div>
    </div>
  );
}
